"use strict";

(() => {
	class Crypto_Profit_Calculator {
		/** @var {HTMLElement} */
		#$widget;

		/** @var {NodeListOf<HTMLInputElement>} */
		#$currenciesInputs;

		/** @var {HTMLInputElement} */
		#$investmentInput;

		/** @var {HTMLInputElement} */
		#$buyPriceInput;

		/** @var {HTMLInputElement} */
		#$buyPriceCurrencyInput;

		/** @var {HTMLButtonElement} */
		#$buyPriceDateButton;

		/** @var {AirDatepicker} */
		#buyPriceDatePicker;

		/** @var {HTMLInputElement} */
		#$sellPriceInput;

		/** @var {HTMLInputElement} */
		#$sellPriceCurrencyInput;

		/** @var {HTMLButtonElement} */
		#$sellPriceDateButton;

		/** @var {AirDatepicker} */
		#sellPriceDatePicker;

		#$includeFeesInput;

		/** @var {HTMLInputElement} */
		#$entryFeeInput;

		/** @var {HTMLInputElement} */
		#$exitFeeInput;

		/** @var {NodeListOf<HTMLElement>} */
		#$currencyLabels;

		/** @var {HTMLElement} */
		#$profitOutput;

		/** @var {HTMLElement} */
		#$totalInvestmentOutput;

		/** @var {HTMLElement} */
		#$totalTakehomeOutput;

		/** @var {object} */
		#exchangeRates;


		constructor($widget, exchangeRates) {
			this.#$widget       = $widget;
			this.#exchangeRates = exchangeRates;
		}

		initialize() {
			// Initialize inputs.
			this.#$currenciesInputs       = this.#$widget.querySelectorAll(".dan-inputs-currency input[type='radio']");
			this.#$investmentInput        = this.#$widget.querySelector(".dan-input-investment");
			this.#$buyPriceInput          = this.#$widget.querySelector(".dan-input-buy-price");
			this.#$buyPriceCurrencyInput  = this.#$widget.querySelector(".dan-input-buy-price-currency");
			this.#$buyPriceDateButton     = this.#$widget.querySelector(".dan-buy-price-calendar");
			this.#$sellPriceInput         = this.#$widget.querySelector(".dan-input-sell-price");
			this.#$sellPriceCurrencyInput = this.#$widget.querySelector(".dan-input-sell-price-currency");
			this.#$sellPriceDateButton    = this.#$widget.querySelector(".dan-sell-price-calendar");
			this.#$includeFeesInput       = this.#$widget.querySelector(".dan-input-include-fees");
			this.#$entryFeeInput          = this.#$widget.querySelector(".dan-input-entry-fee");
			this.#$exitFeeInput           = this.#$widget.querySelector(".dan-input-exit-fee");
			this.#$currencyLabels         = this.#$widget.querySelectorAll(".dan-currency");
			this.#$profitOutput           = this.#$widget.querySelector(".dan-output-profit");
			this.#$totalInvestmentOutput  = this.#$widget.querySelector(".dan-output-total-investment");
			this.#$totalTakehomeOutput    = this.#$widget.querySelector(".dan-output-total-takehome");

			// Attach handlers to events.
			for (const $input of this.#$currenciesInputs) {
				$input.addEventListener("change", (this.#onCurrencyChange).bind(this));
			}

			this.#$investmentInput.addEventListener("input", (this.#recalc).bind(this));
			this.#$buyPriceInput.addEventListener("input", (this.#recalc).bind(this));
			this.#$sellPriceInput.addEventListener("input", (this.#recalc).bind(this));
			this.#$includeFeesInput.addEventListener("change", (this.#refreshFeesVisibility).bind(this));
			this.#$includeFeesInput.addEventListener("change", (this.#recalc).bind(this));
			this.#$entryFeeInput.addEventListener("input", (this.#recalc).bind(this));
			this.#$exitFeeInput.addEventListener("input", (this.#recalc).bind(this));

			this.#$buyPriceCurrencyInput.addEventListener(
				"change",
				(this.#onBuySellCurrencyChange).bind(this)
			);

			this.#$sellPriceCurrencyInput.addEventListener(
				"change",
				(this.#onBuySellCurrencyChange).bind(this)
			);

			// Initialize Air Datepickers.
			// noinspection JSUnusedGlobalSymbols
			this.#buyPriceDatePicker = new AirDatepicker(this.#$buyPriceInput, {
				showEvent: "no-event",
				position: "bottom center",
				view: "years",
				locale: AirDatepickerLocaleCs,
				keyboardNav: false,
				onBeforeSelect: (payload) => {
					this.#onPriceDateSelected(
						payload.date,
						this.#buyPriceDatePicker,
						this.#$buyPriceInput
					);

					return false;
				}
			});

			// noinspection JSUnusedGlobalSymbols
			this.#sellPriceDatePicker = new AirDatepicker(this.#$sellPriceInput, {
				showEvent: "no-event",
				position: "bottom center",
				view: "years",
				locale: AirDatepickerLocaleCs,
				keyboardNav: false,
				onBeforeSelect: (payload) => {
					this.#onPriceDateSelected(
						payload.date,
						this.#sellPriceDatePicker,
						this.#$sellPriceInput
					);

					return false;
				}
			});

			this.#$buyPriceDateButton.addEventListener("click", () => {
				this.#onPriceDateButtonClick(
					this.#buyPriceDatePicker,
					this.#$buyPriceInput
				);
			});

			this.#$sellPriceDateButton.addEventListener("click", () => {
				this.#onPriceDateButtonClick(
					this.#sellPriceDatePicker,
					this.#$sellPriceInput
				);
			});

			// Initialize tooltips.
			const $tooltips = this.#$widget.querySelectorAll("[data-tooltip]");

			if ($tooltips.length > 0) {
				// noinspection JSUnresolvedReference
				tippy($tooltips, {
					content: ($element) => $element.getAttribute("data-tooltip"),
					allowHTML: true
				});
			}

			// Recalc on load.
			this.#recalc();
		}

		#recalc() {
			const inputs = this.getInputs();

			if (
				inputs.investment === null
				|| inputs.buyPrice === null
				|| inputs.sellPrice === null
				|| inputs.entryFee === null
				|| inputs.exitFee === null
			) {
				this.#setOutputValues(null, null, null, null, inputs.currency);
				return;
			}

			const rawProfitPercent  = ((100 / inputs.buyPrice * inputs.sellPrice) - 100);
			const rawProfitAbsolute = (inputs.investment * (rawProfitPercent / 100));

			const totalInvestment = (inputs.investment + inputs.entryFee);
			const totalTakehome   = (inputs.investment + rawProfitAbsolute - inputs.exitFee);
			const profitAbsolute  = (totalTakehome - totalInvestment);
			const profitPercent   = (100 / totalInvestment * profitAbsolute);

			this.#setOutputValues(
				profitAbsolute,
				profitPercent,
				totalInvestment,
				totalTakehome,
				inputs.currency
			);
		}

		#setOutputValues(
			profitAbsolute,
			profitPercent,
			totalInvestment,
			totalTakehome,
			currency
		) {
			// Profit.
			if (profitAbsolute !== null && profitPercent !== null) {
				const profitPercentFormat  = (Math.round(profitPercent * 100) / 100);
				const profitAbsoluteFormat = Dan_Utilities.formatPriceByCurrency(
					profitAbsolute,
					currency
				);

				this.#$profitOutput.textContent =
					profitAbsoluteFormat
					+ " (" + profitPercentFormat + " %)";

				if (profitAbsolute >= 0) {
					this.#$profitOutput.classList.add("-positive");
					this.#$profitOutput.classList.remove("-negative");
				} else {
					this.#$profitOutput.classList.add("-negative");
					this.#$profitOutput.classList.remove("-positive");
				}
			} else {
				this.#$profitOutput.textContent = "—";
				this.#$profitOutput.classList.remove("-positive");
				this.#$profitOutput.classList.remove("-negative");
			}

			// Total investment.
			if (totalInvestment !== null) {
				this.#$totalInvestmentOutput.textContent = Dan_Utilities.formatPriceByCurrency(
					totalInvestment,
					currency
				);
			} else {
				this.#$totalInvestmentOutput.textContent = "—";
			}

			if (totalTakehome !== null) {
				this.#$totalTakehomeOutput.textContent = Dan_Utilities.formatPriceByCurrency(
					totalTakehome,
					currency
				);
			} else {
				this.#$totalTakehomeOutput.textContent = "—";
			}
		}

		#onCurrencyChange() {
			const inputs = this.getInputs();
			const symbol = Dan_Utilities.getCurrencySymbol(inputs.currency);

			for (const $label of this.#$currencyLabels) {
				$label.textContent = symbol;
			}

			this.#recalc();
		}

		#onBuySellCurrencyChange(event) {
			const newCurrency = event.target.value;

			if (!newCurrency) {
				return;
			}

			this.#$buyPriceCurrencyInput.value  = newCurrency;
			this.#$sellPriceCurrencyInput.value = newCurrency;

			this.#recalc();
		}

		#onPriceDateButtonClick(datePicker, $input) {
			// noinspection JSUnresolvedReference
			datePicker.setCurrentView("years");
			// noinspection JSUnresolvedReference
			datePicker.show();
			$input.focus();
		}

		#onPriceDateSelected(date, datePicker, $input) {
			// Hide date picker.
			// noinspection JSUnresolvedReference
			datePicker.hide();

			// Set price to the input.
			const inputs = this.getInputs();
			const price  = this.#getPriceForDate(date, inputs.buySellCurrency);

			if (price === null) {
				alert("Bohužel se nepodařilo zjistit cenu pro vybraný den.");
				return;
			}

			$input.value = Math.round(price).toString();
			$input.dispatchEvent(new Event("input", {
				bubbles: true
			}));
		}

		#getPriceForDate(date, currency) {
			let price = this.#getExchangeRate("BTC", "USD", date);

			if (price === null) {
				return null;
			}

			if (currency !== "USD") {
				const exchangeRate = this.#getExchangeRate(currency, "USD", date);

				if (exchangeRate === null) {
					return null;
				}

				price /= exchangeRate;
			}

			return price;
		}

		#getExchangeRate(assetBase, assetQuote, date) {
			const assetPair  = assetBase + "/" + assetQuote;
			const dateFormat = window.dayjs(date).format("YYYY-MM-DD");

			let rate;

			if (typeof this.#exchangeRates["exact"][assetPair][dateFormat] === "number") {
				rate = this.#exchangeRates["exact"][assetPair][dateFormat];
			} else if (typeof this.#exchangeRates["medians"][assetPair] === "number") {
				rate = this.#exchangeRates["medians"][assetPair];
			} else {
				rate = this.#getClosestExchangeRate(assetBase, assetQuote, date);
			}

			return rate;
		}

		#getClosestExchangeRate(assetBase, assetQuote, refDateString) {
			const assetPair = assetBase + "/" + assetQuote;
			const refDate   = new Date(refDateString);

			let closestDate = null;
			let closestDiff = null;

			for (const iDateString in this.#exchangeRates["exact"][assetPair]) {
				const iDate     = new Date(iDateString);
				const datesDiff = Math.abs(refDate - iDate);

				if (closestDiff === null || datesDiff < closestDiff) {
					closestDate = iDateString;
					closestDiff = datesDiff;
				}
			}

			return this.#exchangeRates["exact"][assetPair][closestDate];
		}

		#refreshFeesVisibility() {
			const $wrapperEntryFee = this.#$widget.querySelector(".dan-wrapper-entry-fee");
			const $wrapperExitFee  = this.#$widget.querySelector(".dan-wrapper-exit-fee");

			const shown    = $wrapperEntryFee.classList.contains("-active");
			const inputs   = this.getInputs();

			if (inputs.includeFees && !shown) {
				$wrapperEntryFee.style.height = "0px";
				$wrapperExitFee.style.height = "0px";

				setTimeout(() => {
					$wrapperEntryFee.style.height = $wrapperEntryFee.scrollHeight + "px";
					$wrapperExitFee.style.height = $wrapperExitFee.scrollHeight + "px";
				}, 25);

				setTimeout(() => {
					$wrapperEntryFee.style.height = "auto";
					$wrapperExitFee.style.height = "auto";

					$wrapperEntryFee.classList.add("-active");
					$wrapperExitFee.classList.add("-active");
				}, 175);
			} else if (!inputs.includeFees && shown) {
				$wrapperEntryFee.style.height = $wrapperEntryFee.scrollHeight + "px";
				$wrapperExitFee.style.height = $wrapperExitFee.scrollHeight + "px";

				setTimeout(() => {
					$wrapperEntryFee.style.height = "0px";
					$wrapperExitFee.style.height = "0px";
				}, 25);

				setTimeout(() => {
					$wrapperEntryFee.classList.remove("-active");
					$wrapperExitFee.classList.remove("-active");
				}, 175);
			}
		}

		getInputs() {
			// Currency.
			let currency = null;

			for (const $input of this.#$currenciesInputs) {
				if ($input.checked) {
					currency = $input.value;
					break;
				}
			}

			// Buy / sell currency.
			const buySellCurrency = this.#$buyPriceCurrencyInput.value;

			// Investment.
			let investment;
			investment = this.#$investmentInput.value || this.#$investmentInput.placeholder;
			investment = parseInt(investment);

			if (isNaN(investment) || investment < 0) {
				investment = null;
			}

			// Buy price.
			let buyPrice;
			buyPrice = this.#$buyPriceInput.value || this.#$buyPriceInput.placeholder;
			buyPrice = parseInt(buyPrice);

			if (isNaN(buyPrice) || buyPrice < 0) {
				buyPrice = null;
			}

			// Sell price.
			let sellPrice;
			sellPrice = this.#$sellPriceInput.value || this.#$sellPriceInput.placeholder;
			sellPrice = parseInt(sellPrice);

			if (isNaN(sellPrice) || sellPrice < 0) {
				sellPrice = null;
			}

			// Include fees.
			const includeFees = (!!this.#$includeFeesInput.checked);

			// Entry fee.
			let entryFee;

			if (includeFees) {
				entryFee = this.#$entryFeeInput.value || this.#$entryFeeInput.placeholder;
				entryFee = parseInt(entryFee);

				if (isNaN(entryFee) || entryFee < 0) {
					entryFee = null;
				}
			} else {
				entryFee = 0;
			}

			// Exit fee.
			let exitFee;

			if (includeFees) {
				exitFee = this.#$exitFeeInput.value || this.#$exitFeeInput.placeholder;
				exitFee = parseInt(exitFee);

				if (isNaN(exitFee) || exitFee < 0) {
					exitFee = null;
				}
			} else {
				exitFee = 0;
			}

			// Return.
			return {
				currency,
				buySellCurrency,
				investment,
				buyPrice,
				sellPrice,
				includeFees,
				entryFee,
				exitFee
			};
		}
	}

	// Initialize widgets.
	const $widgets = document.querySelectorAll(".dan-crypto-profit-calculator");

	for (const $widget of $widgets) {
		let exchangeRates;
		exchangeRates = $widget.getAttribute("data-exchange-rates") || null;
		exchangeRates = (exchangeRates)
			? JSON.parse(exchangeRates)
			: null;

		$widget.calculator = new Crypto_Profit_Calculator($widget, exchangeRates);
		$widget.calculator.initialize();
	}
})();
